import {
  PROFILE_PROPERTY,
  STAGE_PROPERTY,
} from '@vivicta/asdp-contract';

export { JOURNEY_PURPOSE_MAX_LENGTH } from '@vivicta/asdp-contract';

/** Repositories on the ASDP onboarding journey carry this topic (journey spec §3.1.1). */
export const ASDP_JOURNEY_TOPIC = 'asdp-journey';

/** Custom properties the org rulesets follow (ADR-0017). */
export const ASDP_PROFILE_PROPERTY = PROFILE_PROPERTY;
export const ASDP_STAGE_PROPERTY = STAGE_PROPERTY;

/** What Start asks to set up. Never prod: it is added by the Gate 4 decision (journey spec §3.1.1 step 5). */
export type StartEnvironmentChoice = { withTest: boolean; withSandbox: boolean };

/** `^[a-z][a-z0-9-]{2,38}$`: lowercase, 3–39 characters (journey spec §3.1). */
export const JOURNEY_NAME_PATTERN = /^[a-z][a-z0-9-]{2,38}$/;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Whether a template opts in to the journey with `spec.asdp.journey: true` (journey spec §9). */
export function journeyOptIn(manifest: unknown): boolean {
  return (
    isObject(manifest) &&
    isObject(manifest.spec) &&
    isObject(manifest.spec.asdp) &&
    manifest.spec.asdp.journey === true
  );
}

/**
 * The template New Application starts from: with the journey on, the first
 * one that opts in, else the first. Templates can share a stack, and the
 * stack picker never switches between those, so this choice is the only way
 * to reach a journey template.
 */
export function startTemplate<T extends { manifest?: unknown }>(
  templates: readonly T[],
  journey: boolean,
): T | undefined {
  return (
    (journey
      ? templates.find(template => journeyOptIn(template.manifest))
      : undefined) ?? templates[0]
  );
}

/** Only the journey templates once one exists: older templates are retired. */
export function journeyTemplates<T extends { manifest?: unknown }>(
  templates: readonly T[],
): T[] {
  const journey = templates.filter(template => journeyOptIn(template.manifest));
  return journey.length > 0 ? journey : [...templates];
}

/** What is wrong with an application name; '' when it is valid. */
export function journeyNameError(name: string): string {
  if (!name) return 'Name the application.';
  if (name.length < 3) return 'Use at least 3 characters.';
  if (name.length > 39) return 'Use at most 39 characters.';
  if (!/^[a-z]/.test(name)) return 'Start with a lowercase letter.';
  if (!JOURNEY_NAME_PATTERN.test(name)) {
    return 'Use lowercase letters, numbers and dashes only.';
  }
  return '';
}

/** The environments Start requests from the IVM: always dev, the first of dev → test → prod, then test and sandbox when ticked. */
export const startEnvironments = ({ withTest, withSandbox }: StartEnvironmentChoice): string[] => [
  'dev',
  ...(withTest ? ['test'] : []),
  ...(withSandbox ? ['sandbox'] : []),
];
